import { useState, useEffect } from "react";
import { useSeo } from "@/lib/seo";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Smartphone, Trophy, Coins, DollarSign, ShieldCheck, ChevronRight, Languages } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import { useT } from "@/lib/i18n";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

const FAQ = [
  ["What is PhonePlay?", "PhonePlay is a free mobile gaming app by Phone Play LLC. Play daily WordVolt and Solitaire challenges, quick skill games, 1v1 duels and tournaments against real opponents, and a Fun Coin arcade — all in your browser."],
  ["Is PhonePlay free?", "Yes. You get a welcome pack of Fun Coins at signup plus daily bonuses, streaks and quests. Fun Coins are for entertainment only, have no monetary value and cannot be exchanged for money."],
  ["How do skill duels work?", "You and a real opponent get the same puzzle or deal. Outcomes are decided by speed, accuracy, memory and timing — scored on our servers, never by bots."],
  ["Who can play?", "PhonePlay is for players 18 and older in the United States. Some contest formats are offered only where permitted and may vary by state."],
  ["Can I play PhonePlay on my phone?", "Yes. PhonePlay is a mobile-first web app that runs in your phone or desktop browser — no download required."],
];

function SeoLanding() {
  return (
    <section aria-label="About PhonePlay" className="relative z-10 max-w-5xl mx-auto px-6 py-16 border-t border-white/5">
      <h2 className="font-display text-3xl sm:text-4xl font-black text-center mb-4">
        <span className="text-gradient-coins">PhonePlay</span> — Free Skill Games, Daily Challenges &amp; Duels
      </h2>
      <p className="text-gray-400 text-center max-w-2xl mx-auto mb-12">
        Daily <strong>WordVolt</strong> and <strong>Solitaire</strong> challenges, quick skill games, <strong>1v1 duels</strong> and
        tournaments against real opponents, plus a Fun Coin arcade — all on your phone, no download.
      </p>

      <div className="grid sm:grid-cols-3 gap-4 mb-16">
        <div className="glass rounded-2xl p-6 border border-amber-500/20">
          <Coins className="w-6 h-6 text-amber-400 mb-3" />
          <h3 className="font-semibold mb-1">Fun Coin Arcade</h3>
          <p className="text-sm text-gray-500">Slots, wheel, plinko, crash and table games played with Fun Coins. Pure entertainment — Fun Coins have no monetary value.</p>
        </div>
        <div className="glass rounded-2xl p-6 border border-emerald-500/20">
          <Trophy className="w-6 h-6 text-emerald-400 mb-3" />
          <h3 className="font-semibold mb-1">Skill Duels &amp; Tournaments</h3>
          <p className="text-sm text-gray-500">Speed Math, WordVolt, Reaction Rush, Memory Matrix and Solitaire Sprint against real opponents. Decided by skill, scored on our servers.</p>
        </div>
        <div className="glass rounded-2xl p-6 border border-cyan-500/20">
          <Smartphone className="w-6 h-6 text-cyan-400 mb-3" />
          <h3 className="font-semibold mb-1">Play on Your Phone</h3>
          <p className="text-sm text-gray-500">A fast mobile-first web app — no download needed. Jump in from any phone or desktop browser.</p>
        </div>
      </div>

      <h2 className="font-display text-2xl font-bold text-center mb-8">Frequently Asked Questions</h2>
      <div className="space-y-4 max-w-3xl mx-auto">
        {FAQ.map(([q, a]) => (
          <div key={q} className="glass rounded-2xl p-5 border border-white/10">
            <h3 className="font-semibold mb-1.5">{q}</h3>
            <p className="text-sm text-gray-400 leading-relaxed">{a}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap justify-center gap-3 mt-10">
        {[["/games", "Games"], ["/how-to-play", "How to Play"], ["/guides", "Game Guides"], ["/blog", "Blog"], ["/about", "About"], ["/contact", "Contact"]].map(([to, label]) => (
          <Link key={to} to={to} data-testid={`landing-link-${to.slice(1)}`}
            className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-gray-300 hover:text-white hover:border-amber-500/30 transition-colors duration-200">{label}</Link>
        ))}
      </div>

      <p className="text-center text-xs text-gray-600 mt-12 pb-20 sm:pb-0 max-w-2xl mx-auto">
        © {new Date().getFullYear()} Phone Play LLC · PhonePlay. <span data-testid="landing-parent-company">A Damani Holdings company. 5675 Jimmy Carter Blvd, Suite 725, Norcross, GA 30071.</span> <span data-testid="landing-footer-disclaimer" className="text-gray-400">18+ · Coins have no monetary value · No real-money gambling.</span> Fun Coins are non-redeemable. Skill games are games of
        skill played for Fun Coins. Please play responsibly. · <a href="mailto:support@phoneplay.net" className="text-cyan-400 hover:underline">support@phoneplay.net</a>
      </p>
    </section>
  );
}

export default function AuthPage() {
  const { user, setUser, loading } = useAuth();
  const { t, lang, toggle } = useT();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", dob: "", region: "NY", phone: "" });
  const [twofa, setTwofa] = useState(null); // null | { hint }
  const [code, setCode] = useState("");
  const [smsOn, setSmsOn] = useState(false);
  const [postSignup, setPostSignup] = useState(false);
  const [phone, setPhone] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [pbusy, setPbusy] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);

  useEffect(() => {
    if (!loading && user) navigate("/duels", { replace: true });
  }, [loading, user, navigate]);

  useSeo({
    title: "PhonePlay — Free Skill Games, Daily Challenges & Fun Coin Arcade",
    path: "/",
    description: "Play free skill games, daily WordVolt and Solitaire challenges, 1v1 duels and a Fun Coin arcade. Free to play by Phone Play LLC — 18+, coins have no monetary value, no real-money gambling.",
  });
  useEffect(() => {
    try { window.posthog?.capture("landing_view", { referrer: document.referrer || "direct" }); } catch (e) { /* noop */ }
    api.get("/auth/sms/status").then(({ data }) => setSmsOn(!!data.enabled)).catch(() => {});
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // PLAY FREE: same signup flow as the form — switch to Sign Up and bring the form into view
  const startFree = () => {
    setTab("register");
    try { window.posthog?.capture("play_free_click"); } catch (e) { /* noop */ }
    setTimeout(() => {
      document.getElementById("auth-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
      document.querySelector('[data-testid="register-name-input"]')?.focus({ preventScroll: true });
    }, 50);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (tab === "login") {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        if (data.requires_2fa) {
          setTwofa({ hint: data.phone_hint });
          toast.info(`Enter the code we sent to ${data.phone_hint}`);
          return;
        }
        setUser(data);
        toast.success(`Welcome back, ${data.name}!`);
      } else {
        const { data } = await api.post("/auth/register", {
          email: form.email, password: form.password, name: form.name,
          dob: form.dob, country: "US", region: form.region, phone: smsConsent && form.phone ? form.phone : null, sms_consent: smsConsent && !!form.phone,
        });
        try { window.posthog?.capture("sign_up", { method: "email", referrer: document.referrer || "direct" }); } catch (e) { /* noop */ }
        toast.success("Account created! +5,000 bonus coins 🎉");
        if (smsOn) { setPendingUser(data); if (form.phone) setPhone(form.phone); setPostSignup(true); return; }
        setUser(data);
      }
      navigate("/duels");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login/2fa", { email: form.email, password: form.password, code });
      setUser(data);
      toast.success(`Welcome back, ${data.name}!`);
      navigate("/duels");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const sendSignupOtp = async () => {
    setPbusy(true);
    try {
      await api.post("/auth/phone/send-otp", { phone });
      setOtpSent(true);
      toast.success("Verification code sent by SMS");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setPbusy(false); }
  };

  const verifySignupOtp = async () => {
    setPbusy(true);
    try {
      const { data } = await api.post("/auth/phone/verify", { code: otp });
      setUser(data);
      toast.success("Phone verified — welcome to PhonePlay!");
      navigate("/duels");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setPbusy(false); }
  };

  const skipPhone = () => {
    if (pendingUser) setUser(pendingUser);
    navigate("/duels");
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/duels";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="bg-[#07080D] text-white">
    <div className="min-h-screen flex flex-col lg:flex-row relative overflow-hidden">  <button data-testid="language-toggle" onClick={toggle}
        className="absolute top-5 right-5 z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs font-semibold hover:bg-white/10 transition-colors duration-200">
        <Languages className="w-4 h-4" /> {lang.toUpperCase()}
      </button>
      {/* Left hero */}
      <div className="lg:w-1/2 relative flex flex-col justify-center p-8 lg:p-16 overflow-hidden">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full blur-3xl opacity-30" style={{ background: "radial-gradient(circle,#8B5CF6,transparent)" }} />
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-20" style={{ background: "radial-gradient(circle,#10B981,transparent)" }} />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative z-10 max-w-lg">
          <div className="mb-6">
            <BrandLogo size="hero" withWordmark wordmarkClass="text-2xl sm:text-3xl" testid="hero-logo-link" />
          </div>
          <h1 data-testid="play-free-headline" className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.0] mb-3">
            <span className="text-gradient-coins">{t("play_free")}</span>
          </h1>
          <p data-testid="play-free-subheadline" className="text-white/90 text-base sm:text-lg font-semibold mb-5">{t("play_free_sub")}</p>
          <button type="button" data-testid="play-free-button" onClick={startFree}
            className="w-full sm:w-auto mb-8 px-10 py-4 rounded-2xl bg-gradient-to-r from-amber-400 via-orange-500 to-purple-600 font-display font-black text-xl sm:text-2xl tracking-wide text-white shadow-[0_0_32px_rgba(245,158,11,0.35)] hover:scale-[1.02] active:scale-[0.99] transition-transform duration-200">
            {t("play_free_cta")}
          </button>
          <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight leading-tight mb-3">
            {t("hero_title_1")} <span className="text-gradient-coins">{t("hero_title_2")}</span>
          </h2>
          <p className="text-gray-400 text-base sm:text-lg mb-4 leading-relaxed">{t("hero_sub")}</p>
          <p data-testid="hero-disclaimer" className="inline-flex flex-wrap items-center gap-x-2 text-xs font-semibold text-amber-300/90 mb-8 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/25">
            <span>18+</span><span className="text-amber-500/60">·</span><span>Coins have no monetary value</span><span className="text-amber-500/60">·</span><span>No real-money gambling</span>
          </p>
          <div className="space-y-4">
            <div className="flex items-center gap-4 glass rounded-2xl p-4 border border-amber-500/20">
              <div className="w-11 h-11 rounded-xl bg-amber-500/10 flex items-center justify-center border border-amber-500/30">
                <Coins className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="font-semibold">{t("fun_casino")}</p>
                <p className="text-sm text-gray-500">{t("fun_casino_sub")}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 glass rounded-2xl p-4 border border-emerald-500/20">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/30">
                <Trophy className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="font-semibold">{t("skill_tourneys")}</p>
                <p className="text-sm text-gray-500">{t("skill_tourneys_sub")}</p>
              </div>
            </div>
          </div>
          <p className="flex items-center gap-2 text-xs text-gray-600 mt-8">
            <ShieldCheck className="w-4 h-4" /> {t("age_notice")}
          </p>
        </motion.div>
      </div>

      {/* Right form */}
      <div className="lg:w-1/2 flex items-center justify-center p-6 lg:p-16 relative z-10">
        <motion.div id="auth-form" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
          className="w-full max-w-md glass rounded-3xl p-8 border border-white/10 scroll-mt-4">
          {postSignup ? (
            <div className="space-y-5" data-testid="signup-phone-step">
              <div className="text-center">
                <Smartphone className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
                <h2 className="font-display text-xl font-bold">Verify your phone</h2>
                <p className="text-sm text-gray-500 mt-1">Secure your account with one-time login codes and security alerts.</p>
              </div>
              {!otpSent ? (
                <>
                  <input data-testid="signup-phone-input" type="tel" autoFocus value={phone}
                    onChange={(e) => setPhone(e.target.value)} placeholder="+1"
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 focus:border-cyan-500 outline-none transition-colors duration-200 font-mono" />
                  <label data-testid="signup-sms-consent" className="flex items-start gap-2.5 text-[11px] leading-relaxed text-gray-400 cursor-pointer">
              <input data-testid="signup-sms-consent-checkbox" type="checkbox" checked={smsConsent} onChange={(e) => setSmsConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/20 bg-black/40 accent-cyan-500" />
              <span>{t("sms_consent")}{" "}
                <Link to="/terms" className="text-cyan-400 hover:underline">{t("nav_terms")}</Link> · <Link to="/privacy" className="text-cyan-400 hover:underline">{t("nav_privacy")}</Link>
              </span>
            </label>
                  <button data-testid="signup-phone-send-button" onClick={sendSignupOtp} disabled={pbusy || !phone || !smsConsent}
                    className="w-full py-3.5 rounded-xl bg-gradient-to-r from-cyan-500 to-emerald-600 font-semibold hover:opacity-90 transition-opacity duration-200 disabled:opacity-50">
                    {pbusy ? "Sending…" : "Send Code"}
                  </button>
                </>
              ) : (
                <>
                  <input data-testid="signup-otp-input" inputMode="numeric" autoFocus value={otp}
                    onChange={(e) => setOtp(e.target.value)} placeholder="123456"
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 focus:border-cyan-500 outline-none transition-colors duration-200 font-mono text-center tracking-[0.5em] text-lg" />
                  <button data-testid="signup-otp-verify-button" onClick={verifySignupOtp} disabled={pbusy || !otp}
                    className="w-full py-3.5 rounded-xl bg-gradient-to-r from-cyan-500 to-emerald-600 font-semibold hover:opacity-90 transition-opacity duration-200 disabled:opacity-50">
                    {pbusy ? "Verifying…" : "Verify & Continue"}
                  </button>
                </>
              )}
              <button type="button" data-testid="signup-phone-skip-button" onClick={skipPhone}
                className="w-full text-xs text-gray-500 hover:text-white transition-colors duration-200">
                Skip for now — verify later in Wallet
              </button>
            </div>
          ) : twofa ? (
            <form onSubmit={submit2fa} className="space-y-5" data-testid="twofa-form">
              <div className="text-center">
                <ShieldCheck className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
                <h2 className="font-display text-xl font-bold">Two-factor verification</h2>
                <p className="text-sm text-gray-500 mt-1">We sent a 6-digit code by SMS to {twofa.hint}.</p>
              </div>
              <input data-testid="twofa-code-input" inputMode="numeric" autoFocus required value={code}
                onChange={(e) => setCode(e.target.value)} placeholder="123456"
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 focus:border-emerald-500 outline-none transition-colors duration-200 font-mono text-center tracking-[0.5em] text-lg" />
              <button data-testid="twofa-submit-button" disabled={busy} type="submit"
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 font-semibold hover:opacity-90 transition-opacity duration-200 disabled:opacity-50">
                {busy ? "Verifying…" : "Verify & Sign In"}
              </button>
              <button type="button" data-testid="twofa-back-button" onClick={() => { setTwofa(null); setCode(""); }}
                className="w-full text-xs text-gray-500 hover:text-white transition-colors duration-200">
                Use a different account
              </button>
            </form>
          ) : (
          <>
          <div className="flex gap-2 p-1 rounded-xl bg-black/40 mb-8">
            {["login", "register"].map((t) => (
              <button key={t} data-testid={`auth-tab-${t}`} onClick={() => setTab(t)}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-200 ${tab === t ? "bg-gradient-to-r from-amber-500 to-purple-600 text-white" : "text-gray-400 hover:text-white"}`}>
                {t === "login" ? "Log In" : "Sign Up"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {tab === "register" && (
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Display name</label>
                <input data-testid="register-name-input" required value={form.name} onChange={upd("name")}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 focus:border-amber-500 outline-none transition-colors duration-200" placeholder="ArenaLegend" />
              </div>
            )}
            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Email</label>
              <input data-testid="auth-email-input" type="email" required value={form.email} onChange={upd("email")}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 focus:border-amber-500 outline-none transition-colors duration-200" placeholder="you@example.com" />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Password</label>
              <input data-testid="auth-password-input" type="password" required value={form.password} onChange={upd("password")}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 focus:border-amber-500 outline-none transition-colors duration-200" placeholder="••••••••" />
            </div>
            {tab === "register" && (<>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1.5 block">Date of birth</label>
                  <input data-testid="register-dob-input" type="date" required value={form.dob} onChange={upd("dob")}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-3 focus:border-amber-500 outline-none transition-colors duration-200 text-gray-300" />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1.5 block">State</label>
                  <select data-testid="register-region-select" value={form.region} onChange={upd("region")}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-3 focus:border-amber-500 outline-none transition-colors duration-200 text-gray-300">
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div data-testid="register-phone-field">
                <label className="text-xs text-gray-400 mb-1.5 block">Mobile number <span className="text-gray-600">(optional — not required to play)</span></label>
                <input data-testid="register-phone-input" type="tel" autoComplete="tel" placeholder="+1" value={form.phone} onChange={upd("phone")}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 focus:border-cyan-500 outline-none transition-colors duration-200 font-mono" />
                <div className="mt-2">
                <label data-testid="register-sms-consent" className="flex items-start gap-2.5 text-[11px] leading-relaxed text-gray-400 cursor-pointer">
              <input data-testid="register-sms-consent-checkbox" type="checkbox" checked={smsConsent} onChange={(e) => setSmsConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/20 bg-black/40 accent-cyan-500" />
              <span>{t("sms_consent")}{" "}
                <Link to="/terms" className="text-cyan-400 hover:underline">{t("nav_terms")}</Link> · <Link to="/privacy" className="text-cyan-400 hover:underline">{t("nav_privacy")}</Link>
              </span>
            </label>
                </div>
              </div>
            </>)}
            <button data-testid="auth-submit-button" disabled={busy} type="submit"
              className="w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-purple-600 font-semibold hover:opacity-90 transition-opacity duration-200 disabled:opacity-50 flex items-center justify-center gap-2">
              {busy ? "Please wait…" : (tab === "login" ? "Enter the Arena" : "Create Account")}
              <ChevronRight className="w-4 h-4" />
            </button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-gray-600">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <button data-testid="google-login-button" onClick={google}
            className="w-full py-3.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors duration-200 flex items-center justify-center gap-3 font-medium">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
            Continue with Google
          </button>
          <p className="text-xs text-gray-600 text-center mt-6">
            By continuing you confirm you are 18+ and agree to our{" "}
            <a href="/terms.html" data-testid="auth-terms-link" className="text-cyan-400 hover:underline">Terms &amp; Conditions</a> and{" "}
            <a href="/privacy.html" data-testid="auth-privacy-link" className="text-cyan-400 hover:underline">Privacy Policy</a>, and to play responsibly.
          </p>
          </>
          )}
        </motion.div>
      </div>
    </div>
    <SeoLanding />
    </div>
  );
}
